import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useViewport, useSelect, useMultiSelect, useSwitch } from '@chilecompra/react-kit'
import { Card, DataTable, MultiSelect, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import DataLayout, { DataLayoutChild, DataLayoutViews } from '../../components/Layouts/DataLayout'
import LineChart from '../../components/Charts/LineChart.v2'
import useDataLayout from '../../components/Layouts/DataLayout.hook'
import useLineChart from '../../components/Charts/LineChart.hooks'
import generateYearsBetween from '../../modules/utils/years'
import { formatAmount, formatPercentage } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { REACT_APP_MP_URL } from '../../config/settings/environment'
import { onGetTradedAmountsThunk } from './ContingenciesTradedAmountsSection.actions'
import { Content, GridContainer, GridItem, Link, Root } from './ContingenciesTradedAmountsSection.styles'
import InfoLabel from '../../components/Labels/InfoLabel'
import TradedAmounts from '../../components/Infographics/TradedAmounts'

/**
 * The ContingencyExpenseSection's container.
 */
const ContingenciesTradedAmountsSection = () => {
  const dispatch = useDispatch()
  const { contingencyOptions, contingencySelected } = useSelector(state => state.contingencies)
  const {
    data: { chartStructure, csvStructure },
    loading
  } = useSelector(state => state.contingenciesTradedAmounts)
  const { latestUpdate } = useSelector(state => state.topNavbar)
  const dataTableRef = useRef()
  const [startYear, setStartYear] = useState(undefined)
  const [endYear, setEndYear] = useState(undefined)

  const getYearOptions = contingencyOptions?.find(option => option.value === contingencySelected)
  const years = generateYearsBetween(endYear, startYear)

  const {
    size: { isUpToMedium }
  } = useViewport()

  const { value: distValue, onChange: handleDistribution } = useSelect({ initialValue: 'amount' })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const { serie: data, category: categories } = useLineChart(chartStructure, distValue)

  const handleTooltipFormatter = useCallback(
    function () {
      const { amount, name, orders, percentageAccumulative, totalAmount, totalOrders } = this.series.userOptions

      return `
        <ul>
          <li>
            - <strong>${currencyValue ? '$USD' : '$'} ${formatAmount(
              distValue === 'amount' ? amount[this.point.index] : totalAmount[this.point.index]
            )}</strong> transados ${distValue === 'amount' ? 'en' : 'a'} ${this.x} del ${name}.
          </li>
          <br />
          <li>
            - Corresponde al <strong>${formatPercentage(percentageAccumulative[this.point.index])}</strong> del monto ${
              distValue === 'amount' ? 'transado' : 'acumulado'
            } durante el año.
          </li>
          <br />
          <li>
            - <strong>${formatAmount(
              distValue === 'amount' ? orders[this.point.index] : totalOrders[this.point.index]
            )}</strong> órdenes de compra ${distValue === 'amount' ? 'durante' : 'a'} ${this.x} del ${name}
          </li>
        </ul>
      `
    },
    [currencyValue, distValue]
  )

  const enhancedData = useMemo(
    () =>
      data.map(datum => {
        const updateData = {
          ...datum,
          amount: currencyValue ? datum?.amountUSD : datum?.amount,
          totalAmount: currencyValue ? datum?.totalAmountUSD : datum?.totalAmount
        }

        return { ...updateData }
      }),
    [currencyValue, data]
  )

  const labels = useMemo(() => enhancedData.map(datum => ({ color: datum.color, name: datum.name })), [data])

  const {
    values: yearsValue,
    onChange: handleYearsChange,
    onDelete: handleYearsDelete
  } = useMultiSelect({
    initialValues: [years[0].value],
    min: 1,
    max: 4,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(onGetTradedAmountsThunk({ contingencyId: contingencySelected, years: updatedValue }))
    }
  })

  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csvStructure, yearsValue],
    captureImageCallback: () => exportToImage('traded-amounts-line-chart', `Traded_Amounts_${yearsValue}`),
    downloadFileCallback: () => exportToCsv(csvStructure, `Traded_Amounts_${yearsValue}`)
  })

  const distribution = [
    { name: 'Mes del año', value: 'amount' },
    { name: 'Acumulativo anual', value: 'totalAmount' }
  ]

  useEffect(() => {
    dispatch(onGetTradedAmountsThunk({ contingencyId: contingencySelected, years: yearsValue }))
  }, [contingencySelected])

  useEffect(() => {
    setStartYear(getYearOptions?.startYear)
    setEndYear(getYearOptions?.endYear)
  }, [getYearOptions])

  return (
    <Root>
      <Content>
        <GridContainer>
          <GridItem xs={12} md={6}>
            <Typography
              color="primary"
              fontSize="18px"
              fontWeight="bold"
              lineHeight="21px"
              margin={isUpToMedium ? '96px 0 0' : '48px 0 24px'}
              variant="body1"
            >
              Montos transados
            </Typography>
            <Typography
              fontSize="34px"
              fontWeight="bold"
              lineHeight="38px"
              margin={isUpToMedium ? '12px 0' : '0'}
              variant="h2"
              width={isUpToMedium ? '485px' : undefined}
            >
              Distribución del gasto público realizado en COVID-19
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={isUpToMedium ? 5 : 12}>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0' : '24px 0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Conoce el comportamiento y evolución de los montos transados en COVID-19.
            </Typography>
            <Typography
              fontSize="18px"
              lineHeight="24px"
              margin={isUpToMedium ? '0 0 48px' : '0'}
              variant="body1"
              width={isUpToMedium ? '448px' : undefined}
            >
              Puedes seleccionar hasta un máximo de 4 años.
            </Typography>
          </GridItem>
          <GridItem xs={isUpToMedium ? 7 : 12}>
            <Typography fontSize="18px" lineHeight="24px" variant="body1" width={isUpToMedium ? '448px' : undefined}>
              Recuerda que estos montos se extraen desde las órdenes de compra que se emiten en{' '}
              <Link href={REACT_APP_MP_URL}>Mercado Público</Link>, y no representan pagos.
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12}>
            <DataLayout
              disableCaptureImage={disableCaptureImage}
              disableDownloadFile={disableDownloadFile}
              header={
                <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                  <GridItem md={4} xs={12} container spacing={4}>
                    <GridItem md={6} xs={12}>
                      <MultiSelect
                        label="AÑO"
                        margin="0 50px 0 0"
                        options={years}
                        values={yearsValue}
                        onChange={handleYearsChange}
                        onDelete={handleYearsDelete}
                      />
                    </GridItem>
                    <GridItem md={6} xs={12}>
                      <Select
                        label="DISTRIBUCIÓN"
                        options={distribution}
                        value={distValue}
                        onChange={handleDistribution}
                      />
                    </GridItem>
                  </GridItem>
                  <GridItem md={2} xs={12}>
                    <Switch
                      checked={currencyValue}
                      color="primary"
                      labelLeft="CLP"
                      labelRight="USD"
                      margin="0 0 0 30px"
                      onChange={handleChangeCurrency}
                    />
                  </GridItem>
                </GridContainer>
              }
              infographic={<TradedAmounts />}
              marginFooter="0 0 96px"
              noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
              ref={dataLayoutRef}
              updatedDate={latestUpdate}
              onCaptureImage={handleCaptureImage}
              onChangeView={handleChangeView}
              onDownloadFile={handleDownloadFile}
            >
              <DataLayoutChild view={DataLayoutViews.CHART}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer>
                    <GridItem xs={12}>
                      <InfoLabel
                        label={`Millones de ${currencyValue ? 'dólares (USD)' : 'pesos (CLP)'}`}
                        margin="0 0 40px 0"
                      />
                      <LineChart
                        categories={categories}
                        data={enhancedData}
                        id="traded-amounts-line-chart"
                        labels={labels}
                        tooltipFormatter={handleTooltipFormatter}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
              <DataLayoutChild view={DataLayoutViews.TABLE}>
                <Card color="blank" elevation="1" margin="0 0 32px">
                  <GridContainer padding="0 0 24px">
                    <GridItem xs={12}>
                      <DataTable
                        enableResults
                        headers={[
                          { label: 'Año', value: 0 },
                          { label: 'Mes', value: 1 },
                          { label: 'Monto por mes', value: 2 },
                          { label: 'Monto acumulado', value: 3 },
                          { label: 'Monto porcentual mensual', value: 4 },
                          { label: 'Monto porcentual acumulado', value: 5 }
                        ]}
                        headersCellsProps={[
                          { enableFilter: true, enableSort: true },
                          { enableFilter: true, enableSort: true },
                          { enableFilter: true, enableSort: true },
                          { enableFilter: true, enableSort: true },
                          { enableFilter: true, enableSort: true },
                          { enableFilter: true, enableSort: true }
                        ]}
                        initialRows={6}
                        loading={loading}
                        records={csvStructure.slice(1)}
                        recordsDecorators={[
                          ([year]) => year,
                          ([, month]) => month,
                          ([, , amount]) => `$${formatAmount(amount)}`,
                          ([, , , amount]) => `$${formatAmount(amount)}`,
                          ([, , , , percentage]) => formatPercentage(percentage),
                          ([, , , , , percentage]) => formatPercentage(percentage)
                        ]}
                        ref={dataTableRef}
                      />
                    </GridItem>
                  </GridContainer>
                </Card>
              </DataLayoutChild>
            </DataLayout>
          </GridItem>
        </GridContainer>
      </Content>
    </Root>
  )
}

export default ContingenciesTradedAmountsSection
